import type { ColoredMaterialSpecification } from "@color-finder/color-finder-api/models";
import { Alert, Button, Grid, Link, Stack, Typography } from "@mui/material";
import { ColorPreviewDisplay } from "components/dataDisplay/ColorPreviewDisplay";
import { NoData } from "components/dataDisplay/NoData";
import { ThicknessDisplay } from "components/dataDisplay/ThicknessDisplay";
import type { FC, PropsWithChildren } from "react";
import { FormatColor, formatColorText } from "service/color-formatting";
import { DocumentationLink } from "../documentationLink/DocumentationLink";
import type { NamedColor } from "../filter/color-filter/ColorFilter";
import { MeasurementCondition } from "./MeasurementCondition";

export interface ColorSpecificationProps {
  specification: ColoredMaterialSpecification;
}

function hasBackground(
  background?: ColoredMaterialSpecification["background"],
) {
  return background && background !== "Without";
}

export const ColorSpecification: FC<ColorSpecificationProps> = ({
  specification,
}) => {
  const namedColor: NamedColor = {
    value: specification.cieLab,
    name: formatColorText(specification.cieLab),
  };

  return (
    <Grid container spacing={1}>
      <LabelRow label="Measurement">
        <MeasurementCondition
          measurementMode={specification.measurementMode}
          gloss={specification.gloss}
        />
      </LabelRow>
      <LabelRow label="Thickness">
        {specification.thickness ? (
          <ThicknessDisplay
            value={specification.thickness.value}
            unit={specification.thickness.unit}
          />
        ) : (
          <NoData />
        )}
      </LabelRow>
      {hasBackground(specification.background) && (
        <LabelRow label="Background">{specification.background}</LabelRow>
      )}
      <LabelRow label="Color">
        <ColorPreviewDisplay
          contrastBorder
          preview={namedColor}
          height="100%"
          width={45}
        ></ColorPreviewDisplay>
      </LabelRow>
      <LabelRow label={`CIE-Lab | ${specification.observer ?? "D65 D10°"}`}>
        <Stack alignItems="start">
          <FormatColor color={specification.cieLab} />
          {specification.warning === undefined && (
            <Link
              href={`/search?cie_lab=${specification.cieLab}&colorSystem=cie_lab&sort=deltaE`}
              target="_blank"
              referrerPolicy="no-referrer"
            >
              <Button
                sx={{ m: 0, p: 0, height: "auto", textAlign: "start" }}
                size="small"
              >
                Search for this color
              </Button>
            </Link>
          )}
        </Stack>
      </LabelRow>
      {specification.yellownessIndex && (
        <LabelRow label="Yellowness Index">
          {specification.yellownessIndex.toFixed(1)}
        </LabelRow>
      )}
      {specification.warning && (
        <Grid size={12}>
          <Alert severity="info">
            <Typography fontSize={"1em"} sx={{ mt: 0, mb: 0.5 }}>
              {specification.warning}
            </Typography>
            <DocumentationLink
              document="measurementConditions"
              label="Learn more about measurement conditions."
            />
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

const LabelRow: FC<PropsWithChildren<{ label: string }>> = ({
  label,
  children,
}) => {
  return (
    <>
      <Grid size={5}>
        <Typography variant="label">{label}</Typography>
      </Grid>
      <Grid size={7}>{children}</Grid>
    </>
  );
};
